import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Flex, Card, Heading, Text, Button } from "@radix-ui/themes";
import { CheckCircle, XCircle } from "lucide-react";

type VerificationState = "verifying" | "success" | "error";

export default function MagicLinkConfirmPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<VerificationState>("verifying");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const token_hash = searchParams.get("token_hash");
        const type = searchParams.get("type");
        const redirectUri = searchParams.get("redirect_uri");

        if (!token_hash || !type) {
          setErrorMessage("Invalid magic link. Missing required parameters.");
          setState("error");

          // Redirect with error if redirect_uri is provided
          if (redirectUri) {
            const url = new URL(redirectUri);
            url.searchParams.set("error", "missing_parameters");
            window.location.href = url.toString();
          }
          return;
        }

        // Verify the OTP
        // const { data, error } = await gotrue().auth.verifyOtp({
        //   token_hash,
        //   type: type as EmailOtpType,
        // });

        // if (error) throw error;

        // // Check for redirect_uri parameter
        // if (redirectUri) {
        //   // Include tokens in the redirect if available
        //   if (data.session?.access_token && data.session?.refresh_token) {
        //     const url = new URL(redirectUri);
        //     url.searchParams.set("access_token", data.session.access_token);
        //     url.searchParams.set("refresh_token", data.session.refresh_token);
        //     url.searchParams.set("token_type", "bearer");
        //     window.location.href = url.toString();
        //   } else {
        //     window.location.href = redirectUri;
        //   }
        // } else {
        //   // No redirect provided - show success message
        //   setState("success");
        // }
      } catch (error) {
        console.error("Error during auth callback:", error);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to verify magic link. The link may have expired."
        );
        setState("error");

        const redirectUri = searchParams.get("redirect_uri");
        if (redirectUri) {
          // Redirect with error parameter after a short delay so user can see the error
          setTimeout(() => {
            const url = new URL(redirectUri);
            url.searchParams.set("error", "authentication_failed");
            window.location.href = url.toString();
          }, 2000);
        }
      }
    };

    handleAuthCallback();
  }, [searchParams]);

  const renderContent = () => {
    switch (state) {
      case "verifying":
        return (
          <>
            <Heading size="5" weight="medium">
              Verifying your login...
            </Heading>
            <Text size="2" color="gray" align="center">
              Please wait while we verify your magic link.
            </Text>
          </>
        );

      case "success":
        return (
          <>
            <CheckCircle size={48} color="green" />
            <Heading size="5" weight="medium" color="green">
              Authentication Successful
            </Heading>
            <Text size="2" color="gray" align="center">
              Your magic link has been verified successfully. You can now close
              this page and return to your application.
            </Text>
          </>
        );

      case "error":
        const redirectUri = searchParams.get("redirect_uri");
        return (
          <>
            <XCircle size={48} color="red" />
            <Heading size="5" weight="medium" color="red">
              Verification Failed
            </Heading>
            <Text size="2" color="gray" align="center">
              {errorMessage}
            </Text>
            <Text size="2" color="gray" align="center">
              The magic link may have expired or already been used. Please
              request a new link from your application.
            </Text>
            {redirectUri && (
              <>
                <Text size="2" color="gray" align="center">
                  Redirecting you back to your application...
                </Text>
                <Button
                  onClick={() => {
                    window.location.href = redirectUri;
                  }}
                  size="3"
                  style={{ marginTop: "8px" }}
                >
                  Continue to Application
                </Button>
              </>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Flex height="100vh" align="center" justify="center">
      <Card size="3" style={{ width: "100%", maxWidth: "28rem" }}>
        <Flex direction="column" align="center" gap="3" p="6">
          {renderContent()}
        </Flex>
      </Card>
    </Flex>
  );
}
